import React, { useState } from 'react';
import { 
  FileText, 
  Download, 
  Printer,
  Building2,
  ShoppingCart,
  Truck,
  Package,
  Calendar
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useNotifications } from '../../contexts/NotificationContext';
import { printService } from '../../services/printService';

function ImpressionsList() {
  const { user } = useAuth();
  const { addNotification } = useNotifications();
  const [activeTab, setActiveTab] = useState<'orders' | 'chantiers'>('orders');
  const [loading, setLoading] = useState(false);

  const handleGeneratePDF = async (type: string, id: string) => {
    try {
      setLoading(true);
      let pdfUrl = '';
      let filename = '';
      
      switch (type) {
        case 'quote':
          pdfUrl = await printService.generateQuotePDF(id);
          filename = `devis_${id}`;
          break;
        case 'order':
          pdfUrl = await printService.generateOrderPDF(id);
          filename = `bon_preparation_${id}`;
          break;
        case 'delivery':
          pdfUrl = await printService.generateDeliveryPDF(id);
          filename = `bon_livraison_${id}`;
          break;
        case 'supplier_order':
          pdfUrl = await printService.generateSupplierOrderPDF(id);
          filename = `commande_fournisseur_${id}`;
          break;
        case 'inventory':
          pdfUrl = await printService.generateInventoryPDF(id);
          filename = `pv_inventaire_${id}`;
          break;
        case 'consumption':
          pdfUrl = await printService.generateConsumptionReportPDF(id, 'mensuel');
          filename = `consommation_${id}`;
          break;
      }
      
      printService.downloadPDF(pdfUrl, filename);
      
      addNotification({
        userId: user?.id || '',
        title: 'PDF généré',
        message: 'Le document a été généré avec succès',
        type: 'success'
      });
    } catch (error) {
      addNotification({
        userId: user?.id || '',
        title: 'Erreur de génération',
        message: 'Impossible de générer le PDF',
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Mock data pour la démonstration
  const mockOrders = [
    { id: 'order-1', type: 'Devis', number: 'DEV-2024-001', date: '2024-12-15', client: 'Chantier Dupont' },
    { id: 'order-2', type: 'Commande', number: 'CMD-2024-001', date: '2024-12-14', client: 'Chantier Martin' },
    { id: 'order-3', type: 'Bon de livraison', number: 'BL-2024-001', date: '2024-12-13', client: 'Chantier Dupont' },
    { id: 'supplier-1', type: 'Commande fournisseur', number: 'CF-2024-001', date: '2024-12-12', client: 'Bois & Matériaux SA' }
  ];

  const mockChantiers = [
    { id: 'chantier-1', code: 'CH-2024-001', name: 'Maison Dupont', period: 'Décembre 2024' },
    { id: 'chantier-2', code: 'CH-2024-002', name: 'Extension Martin', period: 'Décembre 2024' }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="glass-card rounded-xl card-hover">
        <div className="px-6 py-6 sm:p-8">
          <div className="flex items-center space-x-4">
            <div className="p-2 bg-gradient-to-br from-gray-500 to-gray-700 rounded-lg shadow-lg">
              <FileText className="h-6 w-6 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900">Impressions</h1>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="glass-card rounded-xl">
        <div className="px-6 py-4 border-b border-gray-200/50">
          <div className="flex space-x-8">
            <button
              onClick={() => setActiveTab('orders')}
              className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'orders'
                  ? 'border-gray-500 text-gray-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Commandes & Devis
            </button>
            <button
              onClick={() => setActiveTab('chantiers')}
              className={`py-2 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'chantiers'
                  ? 'border-gray-500 text-gray-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Rapports Chantiers
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="min-h-96">
          {activeTab === 'orders' && (
            <div className="p-6">
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {/* Templates de documents */}
                <div className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-md transition-shadow">
                  <div className="flex items-center mb-4">
                    <FileText className="h-8 w-8 text-blue-500 mr-3" />
                    <h3 className="text-lg font-medium text-gray-900">Devis</h3>
                  </div>
                  <p className="text-sm text-gray-600 mb-4">
                    Générer des devis avec logo, coordonnées et signatures
                  </p>
                  <button
                    onClick={() => handleGeneratePDF('quote', 'demo-quote')}
                    disabled={loading}
                    className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white px-4 py-2 text-sm rounded-lg font-medium transition-colors flex items-center justify-center space-x-2"
                  >
                    <Download className="h-4 w-4" />
                    <span>Générer exemple</span>
                  </button>
                </div>

                <div className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-md transition-shadow">
                  <div className="flex items-center mb-4">
                    <Package className="h-8 w-8 text-orange-500 mr-3" />
                    <h3 className="text-lg font-medium text-gray-900">Bon de préparation</h3>
                  </div>
                  <p className="text-sm text-gray-600 mb-4">
                    Documents pour la préparation des commandes
                  </p>
                  <button
                    onClick={() => handleGeneratePDF('order', 'demo-order')}
                    disabled={loading}
                    className="w-full bg-orange-600 hover:bg-orange-700 disabled:opacity-50 text-white px-4 py-2 text-sm rounded-lg font-medium transition-colors flex items-center justify-center space-x-2"
                  >
                    <Download className="h-4 w-4" />
                    <span>Générer exemple</span>
                  </button>
                </div>

                <div className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-md transition-shadow">
                  <div className="flex items-center mb-4">
                    <Truck className="h-8 w-8 text-green-500 mr-3" />
                    <h3 className="text-lg font-medium text-gray-900">Bon de livraison</h3>
                  </div>
                  <p className="text-sm text-gray-600 mb-4">
                    Documents de livraison avec détails des articles
                  </p>
                  <button
                    onClick={() => handleGeneratePDF('delivery', 'demo-delivery')}
                    disabled={loading}
                    className="w-full bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white px-4 py-2 text-sm rounded-lg font-medium transition-colors flex items-center justify-center space-x-2"
                  >
                    <Download className="h-4 w-4" />
                    <span>Générer exemple</span>
                  </button>
                </div>

                <div className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-md transition-shadow">
                  <div className="flex items-center mb-4">
                    <ShoppingCart className="h-8 w-8 text-purple-500 mr-3" />
                    <h3 className="text-lg font-medium text-gray-900">Commande fournisseur</h3>
                  </div>
                  <p className="text-sm text-gray-600 mb-4">
                    Commandes à envoyer aux fournisseurs
                  </p>
                  <button
                    onClick={() => handleGeneratePDF('supplier_order', 'demo-supplier')}
                    disabled={loading}
                    className="w-full bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white px-4 py-2 text-sm rounded-lg font-medium transition-colors flex items-center justify-center space-x-2"
                  >
                    <Download className="h-4 w-4" />
                    <span>Générer exemple</span>
                  </button>
                </div>

                <div className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-md transition-shadow">
                  <div className="flex items-center mb-4">
                    <Package className="h-8 w-8 text-teal-500 mr-3" />
                    <h3 className="text-lg font-medium text-gray-900">PV d'inventaire</h3>
                  </div>
                  <p className="text-sm text-gray-600 mb-4">
                    Procès-verbal d'inventaire avec écarts
                  </p>
                  <button
                    onClick={() => handleGeneratePDF('inventory', 'demo-inventory')}
                    disabled={loading}
                    className="w-full bg-teal-600 hover:bg-teal-700 disabled:opacity-50 text-white px-4 py-2 text-sm rounded-lg font-medium transition-colors flex items-center justify-center space-x-2"
                  >
                    <Download className="h-4 w-4" />
                    <span>Générer exemple</span>
                  </button>
                </div>
              </div>

              {/* Recent Documents */}
              <div className="mt-8">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Documents récents</h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50/50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Type
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Numéro
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Client/Fournisseur
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Date
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white/50 divide-y divide-gray-200">
                      {mockOrders.map((order) => (
                        <tr key={order.id} className="hover:bg-gray-50/50 transition-colors">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {order.type}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {order.number}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {order.client}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {new Date(order.date).toLocaleDateString('fr-FR')}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <button
                              onClick={() => handleGeneratePDF(order.type.toLowerCase().replace(' ', '_'), order.id)}
                              className="text-blue-600 hover:text-blue-900 p-1 rounded"
                              title="Générer PDF"
                            >
                              <Download className="h-4 w-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'chantiers' && (
            <div className="p-6">
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                {mockChantiers.map((chantier) => (
                  <div key={chantier.id} className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-md transition-shadow">
                    <div className="flex items-center mb-4">
                      <Building2 className="h-8 w-8 text-purple-500 mr-3" />
                      <div>
                        <h3 className="text-lg font-medium text-gray-900">{chantier.name}</h3>
                        <p className="text-sm text-gray-500">{chantier.code}</p>
                      </div>
                    </div>
                    
                    <div className="space-y-3">
                      <button
                        onClick={() => handleGeneratePDF('consumption', chantier.id)}
                        disabled={loading}
                        className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white px-4 py-2 text-sm rounded-lg font-medium transition-colors flex items-center justify-center space-x-2"
                      >
                        <FileText className="h-4 w-4" />
                        <span>État des consommations</span>
                      </button>
                      
                      <button
                        onClick={() => handleGeneratePDF('consumption', chantier.id)}
                        disabled={loading}
                        className="w-full bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white px-4 py-2 text-sm rounded-lg font-medium transition-colors flex items-center justify-center space-x-2"
                      >
                        <Calendar className="h-4 w-4" />
                        <span>Récap mensuel {chantier.period}</span>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ImpressionsList;